import _isEqual from 'lodash/isEqual';
import _cloneDeep from 'lodash/cloneDeep';
import { bool, func, object, shape } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';

import { toast } from 'ravenjs/lib/Toaster';
import { get } from 'ravenjs/utils/lodash';
import { addSchemaValue } from 'ravenjs/utils/schema';
import Form from 'ravenjs/lib/Form';

import { PRODUCT_BANNER_TYPES } from 'constants/banners';
import { DateField } from 'components/Form/fields';
import MESSAGES from 'constants/messages';
import { MODALS } from 'constants/modals';
import REGEX_VALIDATE from 'constants/regex';
import { actions as adminActions } from 'modules/admin';
import { actions as uiActions, selectors as uiSelectors } from 'modules/ui';
import { formatBannerFormData } from 'utils/banners';
import { getTimeStampFromDateTimeMeridian } from 'utils/date';
import { extractErrorMessage } from 'utils/errors';

import Modal from '../Modal';
import UpdateBannerFormSchema from './UpdateBannerFormSchema.json';

const UpdateBannerModalStyled = styled.div`
    position: relative;
`;

class UpdateBannerModal extends Component {
    static propTypes = {
        closeModal: func.isRequired,
        isLoading: func.isRequired,
        modal: shape({
            data: object,
            open: bool,
        }),
        openModal: func.isRequired,
        updateBanner: func.isRequired,
    };

    static defaultProps = {
        modal: {
            open: false,
            data: null,
        },
    };

    constructor(props) {
        super(props);
        const { modal } = this.props;
        const bannerDetails = get(modal, 'data.bannerDetails', {});

        this.state = {
            bannerDetails,
            formErrors: [],
            updatedBannerDetails: formatBannerFormData(bannerDetails),
        };
    }

    componentDidUpdate = prevProps => {
        const { modal: prevModal } = prevProps;
        const { modal } = this.props;
        const bannerDetails = get(modal, 'data.bannerDetails', {});

        if (modal && modal.open && !_isEqual(prevModal, modal)) {
            const productType = get(modal, 'data.bannerDetails.category', '');
            if (
                [PRODUCT_BANNER_TYPES.LEARN, PRODUCT_BANNER_TYPES.HANDBOOKS].indexOf(productType) >=
                0
            ) {
                addSchemaValue(
                    UpdateBannerFormSchema,
                    'schema.properties.bannerStatus.properties.dashboardVisibility.readOnly',
                    false
                );
            } else {
                addSchemaValue(
                    UpdateBannerFormSchema,
                    'schema.properties.bannerStatus.properties.dashboardVisibility.readOnly',
                    true
                );
            }
            this.updateBannerDetails(bannerDetails);
        }
    };

    updateBannerDetails = bannerDetails => {
        this.setState({
            bannerDetails,
            updatedBannerDetails: formatBannerFormData(bannerDetails),
        });
    };

    transformErrors = errors => {
        return errors.map(error => {
            const name = get(error, 'name');
            const format = get(error, 'params.format');
            if (name === 'format' && format === 'onlySpaces') {
                error.message = 'Blank message not allowed!';
            }
            return error;
        });
    };

    getUpdateBannerModalActions = () => {
        const { formErrors } = this.state;

        return [
            {
                onClick: this.handleOnCancel,
                text: 'Cancel',
                color: 'cancelButton',
            },
            {
                onClick: this.handleOnSave,
                text: 'Save',
                color: 'primary',
                acl: !formErrors.length,
            },
        ];
    };

    getModalProps = () => {
        const { modal } = this.props;
        const open = get(modal, 'open', false);

        return {
            actions: this.getUpdateBannerModalActions(),
            content: this.renderContent(),
            id: MODALS.UPDATE_BANNER,
            open,
        };
    };

    handleOnCancel = () => {
        const { closeModal } = this.props;
        this.setState({
            formErrors: [],
        });
        closeModal(MODALS.UPDATE_BANNER);
    };

    handleOnSave = async () => {
        const { closeModal, isLoading, modal, openModal, updateBanner } = this.props;
        const {
            bannerDetails: { id, visibleToRoles, internalId },
            updatedBannerDetails,
        } = this.state;
        const onUpdateBannerSuccess = get(modal, 'data.onUpdateBannerSuccess');

        try {
            isLoading(true, MESSAGES.LOADING.FETCH_BANNERS);
            await updateBanner({ ...updatedBannerDetails, id, visibleToRoles, internalId });
            toast.success(MESSAGES.SUCCESS.UPDATE_BANNER);
            onUpdateBannerSuccess();
            closeModal(MODALS.UPDATE_BANNER);
        } catch (e) {
            const errorMessage = extractErrorMessage(e);

            openModal(MODALS.COMMON, {
                title: 'Error',
                description: errorMessage,
                actions: [
                    {
                        text: 'OK',
                        color: 'primary',
                        onClick() {
                            closeModal(MODALS.COMMON);
                        },
                    },
                ],
            });
        } finally {
            isLoading(false);
        }
    };

    handleFormOnSubmit = ({ formData }) => {
        this.setState(() => ({ updatedBannerDetails: formData }), this.handleOnSave);
    };

    handleFormChange = ({ formData, errors }) => {
        this.setState(() => ({
            updatedBannerDetails: formData,
            formErrors: errors,
        }));
    };

    handleFormValidate = (formData, errors) => {
        const { from, until } = formData;
        const fromDate = getTimeStampFromDateTimeMeridian(from);
        const tillDate = getTimeStampFromDateTimeMeridian(until);
        const formErrors = _cloneDeep(errors);

        if (fromDate > tillDate) {
            formErrors.from.addError('Invalid Dates');
        }
        return formErrors;
    };

    renderUpdateBannerForm = () => {
        const { updatedBannerDetails } = this.state;
        const customFormats = {
            onlySpaces: REGEX_VALIDATE.ONLY_SPACES,
        };

        return (
            <Form
                {...UpdateBannerFormSchema}
                customFormats={customFormats}
                transformErrors={this.transformErrors}
                hideActions
                liveValidate
                showErrorList={false}
                fields={{ DateField }}
                formData={updatedBannerDetails}
                onChange={this.handleFormChange}
                onSubmit={this.handleFormOnSubmit}
                validate={this.handleFormValidate}
            />
        );
    };

    renderContent = () => (
        <UpdateBannerModalStyled innerRef={this.rootRef}>
            {this.renderUpdateBannerForm()}
        </UpdateBannerModalStyled>
    );

    render() {
        return <Modal {...this.getModalProps()} />;
    }
}

const mapStateToProps = createStructuredSelector({
    modal: uiSelectors.getModalById(MODALS.UPDATE_BANNER),
});

const mapDispatchToProps = {
    updateBanner: adminActions.updateBanner,
    closeModal: uiActions.closeModal,
    openModal: uiActions.openModal,
    isLoading: uiActions.isLoading,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateBannerModal);
